import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthProvider';
import { UserRoleProvider, useUserRole } from './context/UserRoleProvider';
import MarketingLayout from './components/layouts/MarketingLayout';
import AppLayout from './components/layouts/AppLayout';
import LandingPage from './components/LandingPage';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AppRedirect from './pages/app/AppRedirect';
import ParishPage from './pages/public/ParishPage';
import Give from './pages/public/Give';
import ParishBrowse from './pages/public/ParishBrowse';
import PublicParishEvents from './pages/public/PublicParishEvents';
import PublicEventDetail from './pages/public/PublicEventDetail';
import DonationComplete from './pages/public/DonationComplete';

// Marketing pages
import HowItWorks from './pages/marketing/HowItWorks';
import ForParishes from './pages/marketing/ForParishes';
import ForDonors from './pages/marketing/ForDonors';
import Security from './pages/marketing/Security';
import Pricing from './pages/marketing/Pricing';
import Privacy from './pages/marketing/Privacy';
import Terms from './pages/marketing/Terms';
import Support from './pages/marketing/Support';

// Donor app pages
import DonorParish from './pages/app/donor/Parish';
import ParishSearch from './pages/app/donor/ParishSearch';
import ParishDetail from './pages/app/donor/ParishDetail';
import DonorDonations from './pages/app/donor/Donations';
import DonorRoundups from './pages/app/donor/Roundups';
import DonorSettings from './pages/app/donor/Settings';
import DonorCheckout from './pages/app/donor/Checkout';
import RecurringDonationSetup from './pages/app/donor/RecurringDonationSetup';
import RecurringDonations from './pages/app/donor/RecurringDonations';
import RecurringDonationEdit from './pages/app/donor/RecurringDonationEdit';

// Parish app pages
import ParishProfile from './pages/app/parish/Profile';
import ParishBank from './pages/app/parish/Bank';
import ParishDonations from './pages/app/parish/Donations';
import ParishSettings from './pages/app/parish/Settings';
import ParishEventsAdmin from './pages/app/parish/Events';
import ParishEventDetailAdmin from './pages/app/parish/EventDetail';
import ParishGroupDetail from './pages/app/parish/GroupDetail';
import ReviewChanges from './pages/app/parish/ReviewChanges';
import AcceptInvite from './pages/app/AcceptInvite';

// Donor event pages
import DonorParishEvents from './pages/app/donor/ParishEvents';
import DonorEventDetail from './pages/app/donor/EventDetail';

// Admin app pages
import AdminDashboard from './pages/app/admin/Dashboard';
import AdminUsers from './pages/app/admin/Users';
import AdminParishes from './pages/app/admin/Parishes';
import AdminParishDetail from './pages/app/admin/ParishDetail';
import CreateParish from './pages/app/admin/CreateParish';
import AdminReports from './pages/app/admin/Reports';
import AdminRefunds from './pages/app/admin/Refunds';

import './App.css';

function CheckoutReturnRedirect() {
  const location = useLocation();
  return <Navigate to={`/donate/return${location.search}`} replace />;
}

function ProtectedRoute({ children, requireParishAdmin = false, allowRoleRedirect = true }) {
  const { session, loading } = useAuth();
  const { isParishAdmin, loading: roleLoading, hasFetched } = useUserRole();
  const location = useLocation();

  // Always wait for both auth and roles to load before making any decisions
  if (loading || roleLoading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="text-charcoal-muted">Loading...</div>
      </div>
    );
  }

  if (!session) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?redirect=${encodeURIComponent(redirect)}`} />;
  }

  // Only redirect if roles are fully loaded AND allowRoleRedirect is true
  // The outer route (AppLayout) should not redirect based on roles
  // IMPORTANT: Only redirect if roles have been fetched at least once (hasFetched)
  // This prevents redirects during initial load or if there was an error fetching roles
  if (allowRoleRedirect && hasFetched) {
    // Only redirect if user doesn't have required role
    // Make sure roles are fully loaded (roleLoading is false) before checking
    // IMPORTANT: Don't redirect if we're already on a parish route - this prevents
    // redirects on page refresh. If user is on /app/parish/bank and refreshes,
    // they should stay there even if roles haven't loaded yet (they'll load in the background)
    const isOnParishRoute = location.pathname.startsWith('/app/parish/');
    if (requireParishAdmin && !isParishAdmin && !roleLoading && !isOnParishRoute) {
      return <Navigate to="/app/donor/parish" replace />;
    }

    // Don't redirect parish admins from donor routes - they should be able to access both
    // Only redirect donors from parish routes if they try to access them
    // (This check is removed to allow parish admins to access donor routes)
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Marketing Routes */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/for-parishes" element={<ForParishes />} />
        <Route path="/for-donors" element={<ForDonors />} />
        <Route path="/security" element={<Security />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/parishes" element={<ParishBrowse />} />
        <Route path="/parishes/:id" element={<ParishPage />} />
        <Route path="/parishes/:id/events" element={<PublicParishEvents />} />
        <Route path="/parishes/:id/events/:eventId" element={<PublicEventDetail />} />
        <Route path="/give/:parishId" element={<Give />} />
        <Route path="/donate/:parishId" element={<DonorCheckout />} />
        <Route path="/donate/return" element={<DonationComplete />} />
      </Route>

      {/* Auth Routes */}
      <Route path="/sign-in" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* App Routes - Protected */}
      <Route
        path="/app"
        element={
          <ProtectedRoute allowRoleRedirect={false}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AppRedirect />} />
        <Route
          path="invites/accept"
          element={
            <ProtectedRoute>
              <AcceptInvite />
            </ProtectedRoute>
          }
        />

        {/* Donor Routes */}
        <Route
          path="donor/parish"
          element={
            <ProtectedRoute>
              <DonorParish />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/parishes/search"
          element={
            <ProtectedRoute>
              <ParishSearch />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/parish/:id"
          element={
            <ProtectedRoute>
              <ParishDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/donations"
          element={
            <ProtectedRoute>
              <DonorDonations />
            </ProtectedRoute>
          }
        />
        <Route path="donor/checkout" element={<Navigate to="/parishes" replace />} />
        <Route path="donor/checkout/return" element={<CheckoutReturnRedirect />} />
        <Route
          path="donor/recurring-donation-setup"
          element={
            <ProtectedRoute>
              <RecurringDonationSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/recurring-donations"
          element={
            <ProtectedRoute>
              <RecurringDonations />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/recurring-donations/:id/edit"
          element={
            <ProtectedRoute>
              <RecurringDonationEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/roundups"
          element={
            <ProtectedRoute>
              <DonorRoundups />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/settings"
          element={
            <ProtectedRoute>
              <DonorSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/parish/:parishId/events"
          element={
            <ProtectedRoute>
              <DonorParishEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="donor/parish/:parishId/events/:eventId"
          element={
            <ProtectedRoute>
              <DonorEventDetail />
            </ProtectedRoute>
          }
        />

        {/* Parish Routes */}
        <Route
          path="parish/profile"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="parish/bank"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishBank />
            </ProtectedRoute>
          }
        />
        <Route
          path="parish/donations"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishDonations />
            </ProtectedRoute>
          }
        />
        <Route
          path="parish/settings"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="parish/settings/:section"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="parish/events"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishEventsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="parish/events/:eventId"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishEventDetailAdmin />
            </ProtectedRoute>
          }
        />
        {/* Groups, Team and Donation Fields now live as tabs under Settings.
            These old paths redirect rather than 404, so bookmarks survive. */}
        <Route path="parish/groups" element={<Navigate to="/app/parish/settings/groups" replace />} />
        <Route
          path="parish/groups/:groupId"
          element={
            <ProtectedRoute requireParishAdmin>
              <ParishGroupDetail />
            </ProtectedRoute>
          }
        />
        <Route path="parish/team" element={<Navigate to="/app/parish/settings/team" replace />} />
        <Route
          path="parish/donation-fields"
          element={<Navigate to="/app/parish/settings/donation-fields" replace />}
        />
        <Route
          path="parish/review-changes/:batchId"
          element={
            <ProtectedRoute requireParishAdmin>
              <ReviewChanges />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="admin/dashboard"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/parishes"
          element={
            <ProtectedRoute>
              <AdminParishes />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/parishes/create"
          element={
            <ProtectedRoute>
              <CreateParish />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/parishes/:id"
          element={
            <ProtectedRoute>
              <AdminParishDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/reports"
          element={
            <ProtectedRoute>
              <AdminReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/refunds"
          element={
            <ProtectedRoute>
              <AdminRefunds />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <UserRoleProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </UserRoleProvider>
    </AuthProvider>
  );
}

export default App;
